import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface KnowledgeArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  updatedAt: string;
}

export interface KnowledgeArticleDetail {
  id: string;
  slug: string;
  title: string;
  content: string;
  category: string;
  roles: string[];
  status: string;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  userFeedback: boolean | null;
  locale: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminKnowledgeArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  roles: string;
  status: string;
  sortOrder: number;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  createdAt: string;
  updatedAt: string;
  translationCount: number;
}

export interface KnowledgeStats {
  totalArticles: number;
  totalViews: number;
  avgHelpful: number;
  topArticles: Array<{
    id: string;
    title: string;
    slug: string;
    viewCount: number;
    helpfulPct: number;
  }>;
}

// ─── Public hooks ─────────────────────────────────────────────────

export function useKnowledgeArticles(category?: string, search?: string) {
  return useQuery({
    queryKey: ['knowledge', 'articles', category, search],
    queryFn: () =>
      apiClient.get<{ success: boolean; data: KnowledgeArticle[] }>(
        `/knowledge/articles?${category && category !== 'all' ? `category=${category}&` : ''}${search ? `search=${encodeURIComponent(search)}` : ''}`
      ),
    select: (res: any) => (res?.data || []) as KnowledgeArticle[],
    staleTime: 60_000,
  });
}

export function useKnowledgeArticle(slug: string | undefined) {
  return useQuery({
    queryKey: ['knowledge', 'article', slug],
    queryFn: () =>
      apiClient.get<{ success: boolean; data: KnowledgeArticleDetail }>(
        `/knowledge/articles/${slug}`
      ),
    select: (res: any) => res?.data as KnowledgeArticleDetail,
    enabled: !!slug,
  });
}

export function useKnowledgeFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, helpful }: { slug: string; helpful: boolean }) =>
      apiClient.post(`/knowledge/articles/${slug}/feedback`, { helpful }),
    onSuccess: (_, { slug }) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'article', slug] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}

// ─── Admin hooks ──────────────────────────────────────────────────

export function useAdminKnowledgeArticles(category?: string, status?: string, search?: string) {
  return useQuery({
    queryKey: ['knowledge', 'admin', 'articles', category, status, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (status && status !== 'all') params.set('status', status);
      if (search) params.set('search', search);
      return apiClient.get<{ success: boolean; data: AdminKnowledgeArticle[] }>(
        `/knowledge/admin/articles?${params.toString()}`
      );
    },
    select: (res: any) => (res?.data || []) as AdminKnowledgeArticle[],
  });
}

export function useKnowledgeStats() {
  return useQuery({
    queryKey: ['knowledge', 'admin', 'stats'],
    queryFn: () => apiClient.get<{ success: boolean; data: KnowledgeStats }>('/knowledge/admin/stats'),
    select: (res: any) => res?.data as KnowledgeStats,
  });
}

export function useCreateKnowledgeArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => apiClient.post('/knowledge/admin/articles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}

export function useUpdateKnowledgeArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => apiClient.put(`/knowledge/admin/articles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}

export function useUpdateKnowledgeArticleStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiClient.patch(`/knowledge/admin/articles/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}

export function useReorderKnowledgeArticles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (articles: Array<{ id: string; sortOrder: number }>) =>
      apiClient.patch('/knowledge/admin/articles/reorder', { articles }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
    },
  });
}

export function useDeleteKnowledgeArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/knowledge/admin/articles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}

export function useUpsertKnowledgeTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ articleId, locale, title, content }: { articleId: string; locale: string; title: string; content: string }) =>
      apiClient.post(`/knowledge/admin/articles/${articleId}/translations`, { locale, title, content }),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin', 'articles'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'article'] });
    },
  });
}

export function useDeleteKnowledgeTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ articleId, locale }: { articleId: string; locale: string }) =>
      apiClient.delete(`/knowledge/admin/articles/${articleId}/translations/${locale}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin', 'articles'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'article'] });
    },
  });
}

// ─── Seed hook ────────────────────────────────────────────────────

export function useSeedKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post('/knowledge/admin/seed'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge', 'articles'] });
    },
  });
}
